import type { CustomAgentResultApprovalStatus } from '@axilon-apps/domains-ontology-ui-state'

import { DenialComposer } from '../insights/DenialComposer'
import { cn } from '../../lib/utils'

export interface CorrectableEvent {
  approval_status?: CustomAgentResultApprovalStatus
  denialNotes?: string
}

export function EventCorrectionButton({
  event,
  isComposing,
  onUndo,
  onToggleComposer
}: {
  event: CorrectableEvent
  isComposing: boolean
  onUndo: () => void
  onToggleComposer: () => void
}) {
  const marked = event.approval_status === 'rejected'
  return (
    <button
      type="button"
      onClick={marked ? onUndo : onToggleComposer}
      aria-expanded={marked ? undefined : isComposing}
      className={cn(
        'inline-flex h-5 cursor-pointer items-center rounded-md border px-2 text-11 leading-none',
        'transition-[background-color,border-color,color] duration-150 ease-out',
        marked
          ? 'border-line bg-bg text-ink-2 hover:bg-bg-sunk'
          : 'border-line bg-bg text-ink-3 hover:border-st-down/60 hover:text-st-down',
        isComposing && !marked && 'border-st-down/60 text-st-down'
      )}
    >
      {marked ? 'This was correct' : 'This was incorrect'}
    </button>
  )
}

export function EventCorrectionDetail({
  event,
  isComposing,
  draft,
  onDraftChange,
  onCancel,
  onConfirm
}: {
  event: CorrectableEvent
  isComposing: boolean
  draft: string
  onDraftChange: (next: string) => void
  onCancel: () => void
  onConfirm: () => void
}) {
  if (event.approval_status === 'rejected') {
    return (
      <div className="mt-2.5 rounded-md border border-st-down/40 bg-st-down-bg/30 p-2.5">
        <div className="text-10 font-medium uppercase tracking-wide text-st-down">
          Marked incorrect
        </div>
        {event.denialNotes ? (
          <p className="m-0 mt-1 whitespace-pre-wrap text-xs text-ink-2">{event.denialNotes}</p>
        ) : null}
      </div>
    )
  }

  if (!isComposing) return null

  return (
    <div className="mt-2.5">
      <DenialComposer
        value={draft}
        onChange={onDraftChange}
        label="What was wrong with this event? Your correction trains the agent."
        placeholder="e.g. the stop was a planned changeover; the tag was in manual…"
        confirmLabel="Mark incorrect"
        onCancel={onCancel}
        onConfirm={onConfirm}
      />
    </div>
  )
}
